import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useIpcQuery } from '../hooks/useIpc';
import { useFeatureFlags } from '../hooks/useFeatureFlags';
import { FoundryShellHeader } from '../components/FoundryShellHeader';
import { useAppFrame } from '../AppFrameContext';
import type {
  VwrEnv,
  VwrPlatform,
  VwrSsoStatus,
  VwrCommandOutput,
} from '@volley/crucible-ipc-types/ipc-channels';
import { unflattenIpcError, type SerialisedError } from '@volley/crucible-ipc-types/error-types';
import { CrucibleErrorState } from '../components/CrucibleErrorState';

/**
 * Devices page — Phase 1, Fire-TV-only happy path.
 *
 * No persistence, no live-config view, no remove. The page does two things:
 *   1. Shows the AWS SSO state and lets the user sign in.
 *   2. Configures a single device against an env's hub.
 *
 * Both the SSO login and the setup flow stream subprocess output back via
 * `vwr:command-output` keyed by a request-id so we can show what's happening
 * without freezing the UI.
 */
export function Devices(): React.JSX.Element {
  const navigate = useNavigate();
  const frame = useAppFrame();

  return (
    <div className="flex h-full flex-col">
      <FoundryShellHeader
        chrome={frame === 'embedded' ? 'web' : 'electron'}
        title="Devices"
        leading={
          <>
            <button
              onClick={() => navigate('/dashboard')}
              className="text-sm text-white/40 transition-colors hover:text-white/80"
            >
              &larr; Back
            </button>
            <h1 className="text-xl font-semibold">Devices</h1>
            <span className="ml-3 rounded-full bg-amber-500/15 px-2.5 py-0.5 text-xs text-amber-300/80">
              Phase 1 · Fire TV only
            </span>
          </>
        }
      />

      <main className="flex-1 overflow-auto px-10 py-8">
        <div className="mx-auto flex max-w-3xl flex-col gap-8">
          <SsoStatusPanel />
          <AddDeviceWizard />
        </div>
      </main>
    </div>
  );
}

/** Devices content without the standalone page layout — used as a Settings tab. */
export function DevicesPanel(): React.JSX.Element {
  const { newUi } = useFeatureFlags();
  return (
    <div className={newUi ? 'space-y-8' : 'mx-auto flex max-w-3xl flex-col gap-8'}>
      {newUi && (
        <h2 className="f-title" style={{ color: 'var(--text-1)' }}>
          Devices
        </h2>
      )}
      <SsoStatusPanel newUi={newUi} />
      <AddDeviceWizard newUi={newUi} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// SSO status pill + sign-in modal
// ---------------------------------------------------------------------------

function SsoStatusPanel({ newUi = false }: { newUi?: boolean }): React.JSX.Element {
  // Two states: `profile` is the input's current value, `appliedProfile` is
  // what the query actually uses. They diverge while the user is typing and
  // re-converge on blur / Enter / explicit Refresh. Without this split, the
  // useIpcQuery key would change every keystroke (Bugbot caught this:
  // typing "volley-tv" spawned 9 concurrent `aws sts` subprocesses).
  const [profile, setProfile] = useState('default');
  const [appliedProfile, setAppliedProfile] = useState('default');
  const [showLogin, setShowLogin] = useState(false);
  const {
    data: status,
    refetch,
    isFetching,
  } = useIpcQuery('vwr:sso-status', { profile: appliedProfile });

  // Commit the input's value to the query. Used by blur, Enter, and the
  // Refresh button. Idempotent: if the input already matches, just refetch.
  const applyProfile = useCallback(() => {
    if (profile !== appliedProfile) {
      setAppliedProfile(profile);
      // useIpcQuery will fire on the new key — no explicit refetch needed.
    } else {
      void refetch();
    }
  }, [profile, appliedProfile, refetch]);

  return (
    <section
      className={
        newUi ? 'rounded-xl p-6' : 'rounded-lg border border-white/10 bg-crucible-surface p-5'
      }
      style={newUi ? { boxShadow: 'var(--hairline)', background: 'var(--bg-elev-2)' } : undefined}
    >
      <div className="flex items-start gap-4">
        <SsoPill status={status} loading={isFetching} />
        <div className="flex-1">
          <h2
            className={newUi ? 'f-title' : 'font-medium'}
            style={newUi ? { color: 'var(--text-1)' } : undefined}
          >
            AWS SSO
          </h2>
          <p
            className={newUi ? 'f-body' : 'text-xs text-white/40'}
            style={newUi ? { color: 'var(--text-3)' } : undefined}
          >
            Required to upload device configs. Account 375633680607 · roles TVDevelopers /
            PlatformFoundations.
          </p>
          {status && !status.loggedIn && status.reason && (
            <p className="mt-2 text-xs text-amber-300/80">{status.reason}</p>
          )}
          {status?.loggedIn && (
            <p
              className={newUi ? 'mt-2 f-body' : 'mt-2 text-xs text-white/50'}
              style={newUi ? { color: 'var(--text-3)' } : undefined}
            >
              Profile <code className="rounded bg-white/5 px-1">{status.profile}</code>
              {status.accountId ? (
                <>
                  {' '}
                  · account <code className="rounded bg-white/5 px-1">{status.accountId}</code>
                </>
              ) : null}
              {status.role ? (
                <>
                  {' '}
                  · role <code className="rounded bg-white/5 px-1">{status.role}</code>
                </>
              ) : null}
            </p>
          )}
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <label
          className={
            newUi
              ? 'flex items-center gap-2 f-body'
              : 'flex items-center gap-2 text-xs text-white/60'
          }
          style={newUi ? { color: 'var(--text-2)' } : undefined}
        >
          AWS profile
          <input
            type="text"
            value={profile}
            onChange={(e) => setProfile(e.target.value)}
            onBlur={applyProfile}
            onKeyDown={(e) => {
              if (e.key === 'Enter') applyProfile();
            }}
            className="rounded-md border border-white/10 bg-white/5 px-2 py-1 text-sm text-white placeholder-white/30 focus:border-crucible-accent focus:outline-none"
            placeholder="default"
          />
        </label>
        <button
          onClick={applyProfile}
          disabled={isFetching}
          className={
            newUi
              ? 'rounded-md px-3 py-1 text-sm transition-colors disabled:opacity-40'
              : 'rounded-md border border-white/10 px-3 py-1 text-sm text-white/70 transition-colors hover:border-white/20 hover:text-white/90 disabled:opacity-40'
          }
          style={
            newUi
              ? {
                  boxShadow: 'var(--hairline)',
                  color: 'var(--text-2)',
                  background: 'var(--bg-elev-3)',
                }
              : undefined
          }
        >
          {isFetching ? 'Checking…' : 'Refresh'}
        </button>
        {status && !status.loggedIn && (
          <button
            onClick={() => setShowLogin(true)}
            className="rounded-md bg-crucible-accent px-3 py-1 text-sm font-medium text-white hover:opacity-90"
          >
            Sign in to AWS
          </button>
        )}
      </div>

      {showLogin && (
        <SsoLoginModal
          profile={appliedProfile}
          onClose={(refreshed) => {
            setShowLogin(false);
            if (refreshed) void refetch();
          }}
        />
      )}
    </section>
  );
}

function SsoPill({
  status,
  loading,
}: {
  status?: VwrSsoStatus;
  loading: boolean;
}): React.JSX.Element {
  if (loading && !status) {
    return (
      <span className="rounded-full bg-white/10 px-3 py-1 text-xs text-white/50">Checking…</span>
    );
  }
  if (!status) {
    return (
      <span className="rounded-full bg-white/10 px-3 py-1 text-xs text-white/50">Unknown</span>
    );
  }
  if (status.loggedIn) {
    return (
      <span className="rounded-full bg-green-500/15 px-3 py-1 text-xs font-medium text-green-300">
        Signed in
      </span>
    );
  }
  return (
    <span className="rounded-full bg-amber-500/15 px-3 py-1 text-xs font-medium text-amber-300">
      Not signed in
    </span>
  );
}

function SsoLoginModal({
  profile,
  onClose,
}: {
  profile: string;
  onClose: (refreshed: boolean) => void;
}): React.JSX.Element {
  const requestIdRef = useRef<string>(crypto.randomUUID());
  // Retry nonce — incrementing this re-fires the sso-login effect.
  // Used by <CrucibleErrorState>'s onRetry on a failed login attempt.
  const [retryNonce, setRetryNonce] = useState(0);
  const mountedRef = useRef(true);
  const [lines, setLines] = useState<{ stream: 'stdout' | 'stderr'; line: string }[]>([]);
  const [stage, setStage] = useState<'running' | 'done' | 'error'>('running');
  // Tier 3: structured error so the modal can show recovery + code via
  // <CrucibleErrorState>. The vwr:sso-login result.error is a plain
  // string today, so wrap with unflattenIpcError to get a generic
  // payload; when the AWS CLI surfaces a CrucibleError, the renderer
  // picks up the rich fields automatically.
  const [error, setError] = useState<SerialisedError | null>(null);
  const [refreshed, setRefreshed] = useState(false);

  // Start the spawn + subscribe to its output.
  //
  // `mountedRef` was previously the only guard, but Macroscope flagged a
  // real bug: when `profile` changes, React runs cleanup (sets ref=false)
  // then the new effect (sets ref=true). The first effect's still-pending
  // `vwr:sso-login` Promise resolves later, sees ref=true, and updates
  // state with the WRONG profile's result. Fix: per-effect `cancelled`
  // local closure, which is bound to the specific effect run and survives
  // dependency changes correctly. Keep `mountedRef` for component-unmount
  // semantics elsewhere.
  useEffect(() => {
    mountedRef.current = true;
    let cancelled = false;
    // Retry generates a fresh requestId so the previous effect's still-
    // pending command-output stream is ignored cleanly.
    if (retryNonce > 0) {
      requestIdRef.current = crypto.randomUUID();
      setStage('running');
      setLines([]);
      setError(null);
    }
    const requestId = requestIdRef.current;
    const unsub = window.crucible.on('vwr:command-output', (evt: VwrCommandOutput) => {
      if (evt.requestId !== requestId) return;
      if (cancelled || !mountedRef.current) return;
      setLines((prev) => [...prev, { stream: evt.stream, line: evt.line }]);
    });

    window.crucible
      .invoke('vwr:sso-login', { requestId, profile })
      .then((result) => {
        if (cancelled || !mountedRef.current) return;
        if (result.success) {
          setStage('done');
          setRefreshed(true);
        } else {
          setStage('error');
          setError(unflattenIpcError(result.error ?? `aws sso login exited ${result.exitCode}.`));
        }
      })
      .catch((err: unknown) => {
        if (cancelled || !mountedRef.current) return;
        setStage('error');
        setError(unflattenIpcError(err));
      });

    return () => {
      cancelled = true;
      mountedRef.current = false;
      unsub();
    };
  }, [profile, retryNonce]);

  const retrySsoLogin = useCallback(() => {
    setRetryNonce((n) => n + 1);
  }, []);

  // Block close while the spawn is still running. Once the AWS CLI has
  // launched, the user can't actually cancel it from here — closing the
  // modal would just abandon the spawn (it'd still complete in the background)
  // and orphan the streamed output. Force the user to wait for the
  // device-grant to resolve or time out.
  const isRunning = stage === 'running';
  const requestClose = (): void => {
    if (isRunning) return;
    onClose(refreshed);
  };

  return (
    // `app-region-no-drag` — the shared fullscreen shell header is the
    // Electron drag surface, so this `fixed inset-0` backdrop needs the
    // opt-out or top-of-window clicks are interpreted as window-drag gestures.
    <div className="app-region-no-drag fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="flex w-full max-w-xl flex-col gap-4 rounded-xl border border-white/10 bg-crucible-surface p-6 shadow-2xl">
        <header className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold">Sign in to AWS</h2>
            <p className="text-xs text-white/40">
              Profile <code className="rounded bg-white/5 px-1">{profile}</code>. AWS will open a
              browser tab — approve the device-grant and return here.
            </p>
          </div>
          <button
            onClick={requestClose}
            disabled={isRunning}
            className="text-sm text-white/40 hover:text-white/80 disabled:cursor-not-allowed disabled:opacity-30"
            aria-label="Close"
            title={isRunning ? 'Wait for AWS to finish before closing' : 'Close'}
          >
            ✕
          </button>
        </header>

        <LogPanel lines={lines} />

        {stage === 'error' && error && (
          <CrucibleErrorState error={error} compact onRetry={retrySsoLogin} />
        )}

        <footer className="flex items-center justify-between gap-3">
          <span className="text-xs text-white/50">
            {stage === 'running' && 'Waiting for AWS…'}
            {stage === 'done' && <span className="text-green-300">Signed in.</span>}
          </span>
          <button
            onClick={requestClose}
            disabled={isRunning}
            className="rounded-md bg-crucible-accent px-3 py-1.5 text-sm font-medium text-white disabled:opacity-40"
          >
            {isRunning ? 'Working…' : 'Close'}
          </button>
        </footer>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Add device wizard (single screen — Phase 1 keeps the form compact)
// ---------------------------------------------------------------------------

const ENV_OPTIONS: { value: VwrEnv; label: string }[] = [
  { value: 'dev', label: 'Development' },
  { value: 'staging', label: 'Staging' },
  { value: 'prod', label: 'Production' },
];

const PLATFORM_OPTIONS: { value: VwrPlatform; label: string }[] = [
  { value: 'FIRE_TV', label: 'Fire TV' },
];

function AddDeviceWizard({ newUi = false }: { newUi?: boolean }): React.JSX.Element {
  const [deviceId, setDeviceId] = useState('');
  const [env, setEnv] = useState<VwrEnv>('dev');
  const [platform] = useState<VwrPlatform>('FIRE_TV'); // locked for Phase 1
  const [launchUrl, setLaunchUrl] = useState('');
  const [profile, setProfile] = useState('default');

  const [running, setRunning] = useState(false);
  const [stage, setStage] = useState<'idle' | 'running' | 'done' | 'error'>('idle');
  const [lines, setLines] = useState<{ stream: 'stdout' | 'stderr'; line: string }[]>([]);
  // Tier 3: structured error for the setup-device flow. Same wrapping
  // pattern as SsoLoginModal — vwr:setup-device returns a string today,
  // unflattenIpcError builds the generic envelope.
  const [error, setError] = useState<SerialisedError | null>(null);
  const requestIdRef = useRef<string | null>(null);
  // Mirrors the SSO modal pattern: if the user navigates away from /devices
  // (page-level Back button, window close, deep-link, etc.) while a setup
  // subprocess is in flight, the async resolve will fire setRunning/setStage
  // against an unmounted component. Guard every async setter with this ref.
  const mountedRef = useRef(true);

  // Subscribe to subprocess output keyed by the active requestId. The
  // listener stays mounted across runs; we filter inside.
  useEffect(() => {
    mountedRef.current = true;
    const unsub = window.crucible.on('vwr:command-output', (evt: VwrCommandOutput) => {
      if (evt.requestId !== requestIdRef.current) return;
      if (!mountedRef.current) return;
      setLines((prev) => [...prev, { stream: evt.stream, line: evt.line }]);
    });
    return () => {
      mountedRef.current = false;
      unsub();
    };
  }, []);

  const canSubmit = deviceId.trim().length > 0 && !running;

  const handleSubmit = useCallback(async () => {
    if (!canSubmit) return;
    setRunning(true);
    setStage('running');
    setLines([]);
    setError(null);
    const requestId = crypto.randomUUID();
    requestIdRef.current = requestId;
    try {
      const result = await window.crucible.invoke('vwr:setup-device', {
        requestId,
        platform,
        deviceId: deviceId.trim(),
        env,
        ...(launchUrl.trim() ? { launchUrl: launchUrl.trim() } : {}),
        ...(profile.trim() && profile.trim() !== 'default' ? { profile: profile.trim() } : {}),
      });
      if (!mountedRef.current) return;
      if (result.success) {
        setStage('done');
      } else {
        setStage('error');
        setError(unflattenIpcError(result.error ?? `vwr-config setup exited ${result.exitCode}.`));
      }
    } catch (err: unknown) {
      if (!mountedRef.current) return;
      setStage('error');
      setError(unflattenIpcError(err));
    } finally {
      if (mountedRef.current) setRunning(false);
    }
  }, [canSubmit, deviceId, env, launchUrl, platform, profile]);

  const reset = (): void => {
    setStage('idle');
    setLines([]);
    setError(null);
    requestIdRef.current = null;
  };

  return (
    <section
      className={
        newUi ? 'rounded-xl p-6' : 'rounded-lg border border-white/10 bg-crucible-surface p-5'
      }
      style={newUi ? { boxShadow: 'var(--hairline)', background: 'var(--bg-elev-2)' } : undefined}
    >
      <h2
        className={newUi ? 'f-title' : 'font-medium'}
        style={newUi ? { color: 'var(--text-1)' } : undefined}
      >
        Configure a device
      </h2>
      <p
        className={newUi ? 'mt-1 f-body' : 'text-xs text-white/40'}
        style={newUi ? { color: 'var(--text-3)' } : undefined}
      >
        Uploads a device config to S3 and enables the VWR Amplitude flag. Cold-restart the TV app
        afterwards. CDN refresh can take 1–3 minutes.
      </p>

      <div className="mt-4 grid gap-4 md:grid-cols-2">
        <div>
          <label
            className={newUi ? 'mb-1 block f-body' : 'mb-1 block text-sm text-white/60'}
            style={newUi ? { color: 'var(--text-2)' } : undefined}
          >
            Device ID
          </label>
          <input
            type="text"
            value={deviceId}
            onChange={(e) => setDeviceId(e.target.value)}
            placeholder="from on-screen debug overlay"
            className="w-full rounded-md border border-white/10 bg-white/5 px-3 py-2 text-sm text-white placeholder-white/30 focus:border-crucible-accent focus:outline-none"
            disabled={running}
          />
        </div>

        <div>
          <label
            className={newUi ? 'mb-1 block f-body' : 'mb-1 block text-sm text-white/60'}
            style={newUi ? { color: 'var(--text-2)' } : undefined}
          >
            Platform
          </label>
          <select
            value={platform}
            disabled
            className="w-full cursor-not-allowed rounded-md border border-white/10 bg-white/5 px-3 py-2 text-sm text-white/60"
          >
            {PLATFORM_OPTIONS.map((p) => (
              <option key={p.value} value={p.value}>
                {p.label}
              </option>
            ))}
          </select>
          <p
            className={newUi ? 'mt-1 f-body' : 'mt-1 text-[11px] text-white/30'}
            style={newUi ? { color: 'var(--text-3)' } : undefined}
          >
            Samsung / LG arrive in Phase 3.
          </p>
        </div>

        <div>
          <label
            className={newUi ? 'mb-1 block f-body' : 'mb-1 block text-sm text-white/60'}
            style={newUi ? { color: 'var(--text-2)' } : undefined}
          >
            Environment
          </label>
          <select
            value={env}
            onChange={(e) => setEnv(e.target.value as VwrEnv)}
            className="w-full rounded-md border border-white/10 bg-white/5 px-3 py-2 text-sm text-white focus:border-crucible-accent focus:outline-none"
            disabled={running}
          >
            {ENV_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label
            className={newUi ? 'mb-1 block f-body' : 'mb-1 block text-sm text-white/60'}
            style={newUi ? { color: 'var(--text-2)' } : undefined}
          >
            AWS profile
          </label>
          <input
            type="text"
            value={profile}
            onChange={(e) => setProfile(e.target.value)}
            className="w-full rounded-md border border-white/10 bg-white/5 px-3 py-2 text-sm text-white focus:border-crucible-accent focus:outline-none"
            disabled={running}
            placeholder="default"
          />
        </div>

        <div className="md:col-span-2">
          <label
            className={newUi ? 'mb-1 block f-body' : 'mb-1 block text-sm text-white/60'}
            style={newUi ? { color: 'var(--text-2)' } : undefined}
          >
            Direct-launch URL{' '}
            <span
              className={newUi ? '' : 'text-white/30'}
              style={newUi ? { color: 'var(--text-3)' } : undefined}
            >
              (optional)
            </span>
          </label>
          <input
            type="text"
            value={launchUrl}
            onChange={(e) => setLaunchUrl(e.target.value)}
            placeholder="leave blank to launch the env's hub"
            className="w-full rounded-md border border-white/10 bg-white/5 px-3 py-2 text-sm text-white placeholder-white/30 focus:border-crucible-accent focus:outline-none"
            disabled={running}
          />
          <p
            className={newUi ? 'mt-1 f-body' : 'mt-1 text-[11px] text-white/30'}
            style={newUi ? { color: 'var(--text-3)' } : undefined}
          >
            For testing a specific game URL on this device. When blank we pass the env's hub URL as
            a sentinel to skip the CLI's interactive prompts.
          </p>
        </div>
      </div>

      <div className="mt-5 flex items-center gap-3">
        <button
          onClick={handleSubmit}
          disabled={!canSubmit}
          className="rounded-md bg-crucible-accent px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
        >
          {running ? 'Configuring…' : 'Configure device'}
        </button>
        {stage === 'done' && (
          <span className="text-sm text-green-300">Done — restart the TV app.</span>
        )}
        {(stage === 'done' || stage === 'error') && (
          <button onClick={reset} className="text-xs text-white/40 hover:text-white/70">
            Configure another
          </button>
        )}
      </div>

      {stage === 'error' && error && (
        <div className="mt-4">
          {/* Retry just re-fires handleSubmit with the current form values.
              canSubmit gates against double-clicking; the form state is
              still populated from the previous attempt. */}
          <CrucibleErrorState error={error} compact onRetry={handleSubmit} />
        </div>
      )}

      {(stage !== 'idle' || lines.length > 0) && (
        <div className="mt-4">
          <LogPanel lines={lines} />
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Shared subprocess log panel
// ---------------------------------------------------------------------------

function LogPanel({
  lines,
}: {
  lines: { stream: 'stdout' | 'stderr'; line: string }[];
}): React.JSX.Element {
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (ref.current) ref.current.scrollTop = ref.current.scrollHeight;
  }, [lines]);

  return (
    <div
      ref={ref}
      className="max-h-64 overflow-auto rounded-md border border-white/10 bg-black/40 p-3 font-mono text-[11px] leading-relaxed"
    >
      {lines.length === 0 ? (
        <span className="text-white/30">(waiting for output)</span>
      ) : (
        lines.map((l, i) => (
          <div key={i} className={l.stream === 'stderr' ? 'text-amber-300/90' : 'text-white/70'}>
            {l.line}
          </div>
        ))
      )}
    </div>
  );
}
